import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Layers, Rocket, Cloud, Brain, Palette, Users, CheckCircle2, ArrowUpRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { services } from '../data/mock';
import ScrollReveal, { StaggerContainer, StaggerItem } from '../components/ui/ScrollReveal';

const iconMap = {
  Layers: Layers,
  Rocket: Rocket,
  Cloud: Cloud,
  Brain: Brain,
  Palette: Palette,
  Users: Users
};

const ServicesPage = () => {
  return (
    <div className="overflow-hidden bg-slate-950">
      {/* Hero Section */}
      <section className="relative pt-32 pb-16">
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-0 left-1/4 w-96 h-96 bg-emerald-500/10 rounded-full blur-3xl" />
          <div className="absolute bottom-0 right-1/4 w-80 h-80 bg-teal-500/10 rounded-full blur-3xl" />
        </div>

        <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
          <div className="max-w-3xl">
            <ScrollReveal animation="fadeDown" delay={0.1}>
              <span className="text-emerald-400 font-semibold text-sm uppercase tracking-wider">Our Services</span>
            </ScrollReveal>
            <ScrollReveal animation="fadeUp" delay={0.2}>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mt-4 mb-6 leading-tight">
                Software Solutions for{' '}
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-emerald-400 to-teal-400">
                  Every Need
                </span>
              </h1>
            </ScrollReveal>
            <ScrollReveal animation="fadeUp" delay={0.3}>
              <p className="text-xl text-slate-400 leading-relaxed">
                From web and mobile development to cloud services and consulting, we provide comprehensive solutions to drive your business forward.
              </p>
            </ScrollReveal>
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="space-y-16">
            {services.map((service, index) => {
              const IconComponent = iconMap[service.icon];
              const isEven = index % 2 === 0;

              return (
                <ScrollReveal
                  key={service.id}
                  animation={isEven ? "fadeRight" : "fadeLeft"}
                  delay={0.1}
                >
                  <div
                    className={`flex flex-col ${isEven ? 'lg:flex-row' : 'lg:flex-row-reverse'} gap-12 items-center`}
                  >
                    {/* Image or Icon Card */}
                    <div className="lg:w-1/3">
                      <div className="relative">
                        <div className="absolute inset-0 bg-gradient-to-br from-emerald-500/20 to-teal-500/20 rounded-3xl transform rotate-3 blur-sm" />
                        <Card className="relative border border-slate-800/50 bg-slate-900/50 rounded-2xl overflow-hidden">
                          {service.title === "Product Development" ? (
                            <img
                              src="/images/product-development.jpg"
                              alt="Product Development Team"
                              className="w-full h-48 object-cover"
                            />
                          ) : service.title === "MVP Development" ? (
                            <img
                              src="/images/mvp-development.jpg"
                              alt="MVP Development"
                              className="w-full h-48 object-cover"
                            />
                          ) : service.title === "SaaS Development" ? (
                            <img
                              src="/images/saas-development.jpg"
                              alt="SaaS Development"
                              className="w-full h-48 object-cover"
                            />
                          ) : service.title === "AI/ML & Data Engineering" ? (
                            <img
                              src="/images/ai-ml-development.jpg"
                              alt="AI/ML & Data Engineering"
                              className="w-full h-48 object-cover"
                            />
                          ) : service.title === "UI/UX Design" ? (
                            <img
                              src="/images/ui-ux-design.png"
                              alt="UI/UX Design"
                              className="w-full h-48 object-cover"
                            />
                          ) : service.title === "Offshore Development" ? (
                            <img
                              src="/images/offshore-development.jpg"
                              alt="Offshore Development"
                              className="w-full h-48 object-cover"
                            />
                          ) : (
                            <CardContent className="p-12 flex items-center justify-center">
                              <div className="w-24 h-24 rounded-3xl bg-gradient-to-br from-emerald-500 to-teal-500 flex items-center justify-center shadow-lg shadow-emerald-500/30">
                                <IconComponent className="w-12 h-12 text-slate-900" />
                              </div>
                            </CardContent>
                          )}
                        </Card>
                      </div>
                    </div>

                    {/* Content */}
                    <div className="lg:w-2/3">
                      <h2 className="text-3xl font-bold text-white mb-4">
                        {service.title}
                      </h2>
                      <p className="text-lg text-slate-400 leading-relaxed mb-6">
                        {service.fullDescription}
                      </p>
                      <StaggerContainer staggerDelay={0.1} className="space-y-3">
                        {getServiceFeatures(service.title).map((feature, i) => (
                          <StaggerItem key={i} animation="fadeUp">
                            <div className="flex items-center gap-3">
                              <CheckCircle2 className="w-5 h-5 text-emerald-400 flex-shrink-0" />
                              <span className="text-slate-400">{feature}</span>
                            </div>
                          </StaggerItem>
                        ))}
                      </StaggerContainer>
                    </div>
                  </div>
                </ScrollReveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-24 border-t border-slate-800/50">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <ScrollReveal animation="fadeUp" className="text-center mb-16">
            <span className="text-emerald-400 font-semibold text-sm uppercase tracking-wider">Our Process</span>
            <h2 className="text-4xl font-bold text-white mt-4 mb-6">How We Work</h2>
            <p className="text-slate-400 max-w-2xl mx-auto text-lg">
              Our proven methodology ensures successful project delivery every time.
            </p>
          </ScrollReveal>

          <StaggerContainer staggerDelay={0.15} className="grid md:grid-cols-4 gap-8">
            {[
              { step: "01", title: "Discovery", description: "We dive deep into understanding your business needs and goals." },
              { step: "02", title: "Planning", description: "We create a detailed roadmap and technical architecture." },
              { step: "03", title: "Development", description: "Our team builds your solution using agile methodology." },
              { step: "04", title: "Launch", description: "We deploy, test, and provide ongoing support." }
            ].map((item, index) => (
              <StaggerItem key={index} animation="scaleUp">
                <div className="relative">
                  {index < 3 && (
                    <div className="hidden md:block absolute top-8 left-full w-full h-0.5 bg-gradient-to-r from-emerald-500/50 to-transparent -translate-x-1/2 z-0" />
                  )}
                  <Card className="relative z-10 border border-slate-800/50 bg-slate-900/50 hover:border-emerald-500/30 transition-all duration-500 rounded-2xl h-full">
                    <CardContent className="p-8 text-center">
                      <div className="w-16 h-16 rounded-full bg-emerald-500/10 flex items-center justify-center mx-auto mb-6 border border-emerald-500/20">
                        <span className="text-emerald-400 font-bold text-xl">{item.step}</span>
                      </div>
                      <h3 className="text-lg font-semibold text-white mb-3">{item.title}</h3>
                      <p className="text-slate-400 text-sm leading-relaxed">{item.description}</p>
                    </CardContent>
                  </Card>
                </div>
              </StaggerItem>
            ))}
          </StaggerContainer>
        </div>
      </section>

      {/* CTA Section */}
      <ScrollReveal animation="scaleUp">
        <section className="py-24">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="relative rounded-3xl bg-gradient-to-br from-slate-900 to-slate-800 p-12 md:p-16 overflow-hidden border border-slate-700/50">
              <div className="absolute top-0 right-0 w-80 h-80 bg-emerald-500/10 rounded-full blur-3xl" />
              <div className="absolute bottom-0 left-0 w-64 h-64 bg-teal-500/10 rounded-full blur-3xl" />

              <div className="relative z-10 max-w-2xl mx-auto text-center">
                <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
                  Ready to Get Started?
                </h2>
                <p className="text-slate-400 text-lg mb-8 leading-relaxed">
                  Contact us today for a free consultation. Let's discuss how we can help bring your vision to life.
                </p>
                <Link to="/contact">
                  <Button size="lg" className="bg-emerald-500 hover:bg-emerald-400 text-slate-900 font-semibold shadow-xl shadow-emerald-500/20 transition-all duration-300 text-base px-8 h-14 rounded-full">
                    Request a Quote
                    <ArrowRight size={18} className="ml-2" />
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </section>
      </ScrollReveal>
    </div>
  );
};

// Helper function to get features for each service
const getServiceFeatures = (serviceTitle) => {
  const features = {
    "Product Development": [
      "End-to-end development lifecycle",
      "Scalable architecture design",
      "Cross-platform solutions",
      "Continuous integration & deployment"
    ],
    "MVP Development": [
      "Rapid prototyping & validation",
      "Lean development approach",
      "Market-ready in weeks",
      "Iterative feedback loops"
    ],
    "SaaS Development": [
      "Multi-tenant architecture",
      "Subscription management",
      "Analytics & reporting",
      "API-first design"
    ],
    "AI/ML & Data Engineering": [
      "Custom ML model development",
      "Data pipeline architecture",
      "Predictive analytics",
      "Intelligent automation"
    ],
    "UI/UX Design": [
      "User research & testing",
      "Wireframing & prototyping",
      "Brand-consistent designs",
      "Accessibility compliance"
    ],
    "Offshore Development": [
      "Dedicated development teams",
      "Seamless integration",
      "Flexible engagement models",
      "24/7 support coverage"
    ]
  };
  return features[serviceTitle] || [];
};

export default ServicesPage;
